import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Loader2, Wrench, Package, Clock, Play, Square, MapPin,
    CalendarClock, RefreshCw, CheckCircle2, Camera, Trash2, Video, ClipboardList, Mic, MicOff,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import WwsAddBar from "../components/wms/WwsAddBar";

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };
const fmtTime = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const isVideoUrl = (u) => /\.(mp4|webm|mov|3gpp?)($|\?)/i.test(String(u || ""));

/**
 * Jun-2026 (User-Wunsch) — Mobile Auftrags-Erfassung (per QR aufrufbar,
 * für eingeloggte Mitarbeiter des Unternehmens sowie Vermittler-Verkäufer).
 * Jul-2026 Überarbeitung: hellere Schrift (Dunkelmodus), Autospeichern,
 * WWS-Universalsuche + EAN-Scan, Schadensfotos + Video, Abstellort nach
 * Reparatur, Monteur-Anmerkungen als Pflicht vor Abrechnungs-Freigabe.
 */
export default function MobileOrderCapturePage() {
    const { wid } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Standort / Fahrzeug (Autosave)
    const [abstellort, setAbstellort] = useState("");
    const [vehicleColor, setVehicleColor] = useState("");
    const [mechaniker, setMechaniker] = useState("");
    // Nach der Reparatur (Autosave)
    const [abstellortAfter, setAbstellortAfter] = useState("");
    // Monteur-Anmerkungen (Pflicht vor Freigabe)
    const [monteurNote, setMonteurNote] = useState("");
    const [monteurNoteNone, setMonteurNoteNone] = useState(false);
    // Jul-2026 (User-Wunsch): Kundenauftrag (im Wortlaut) als bearbeitbares Freitextfeld
    const [complaintText, setComplaintText] = useState("");
    // Jul-2026 (User-Wunsch): fehlende Pflichtfelder beim Abschluss hervorheben
    const [missing, setMissing] = useState([]);
    const [uploadingAbstAfter, setUploadingAbstAfter] = useState(false);
    const metaLoaded = useRef(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/work-orders/${wid}/capture`);
            setData(r.data);
            const cm = r.data.capture_meta || {};
            setAbstellort(cm.abstellort || "");
            setVehicleColor(cm.vehicle_color || "");
            setMechaniker(cm.mechaniker || "");
            setAbstellortAfter(cm.abstellort_after || "");
            setMonteurNote(cm.monteur_note || "");
            setMonteurNoteNone(!!cm.monteur_note_none);
            setComplaintText(r.data.order?.customer_complaint || r.data.delivery_meta?.customer_complaint || "");
            metaLoaded.current = true;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Auftrag konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    }, [wid]);

    useEffect(() => {
        if (authLoading) return;
        if (user === false || !user) {
            navigate(`/login?next=${encodeURIComponent(`/werkstatt/erfassen/${wid}`)}`);
            return;
        }
        load();
    }, [authLoading, user, wid, navigate, load]);

    // Jul-2026 (User-Wunsch): AUTOSPEICHERN — debounced PATCH bei jeder Änderung.
    const autosaveTimer = useRef(null);
    const autosave = useCallback((fields) => {
        if (!metaLoaded.current) return;
        clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(async () => {
            try {
                await api.patch(`/work-orders/${wid}/capture-meta`, fields);
            } catch { toast.error("Automatisches Speichern fehlgeschlagen"); }
        }, 700);
    }, [wid]);

    // WWS-Universalsuche + EAN (Jul-2026, User-Wunsch)
    const quickAdd = async (payload) => {
        setSaving(true);
        try {
            await api.post(`/work-orders/${wid}/positions/quick-add`, payload);
            toast.success("Position erfasst");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erfassen fehlgeschlagen");
        } finally { setSaving(false); }
    };
    const addArticle = (art) => {
        if (!art) return;
        const gross = Number(art.sell_price_gross ?? ((art.sell_price_net || 0) * (1 + (art.vat_rate || 19) / 100)) ?? 0);
        quickAdd({
            type: art.category === "labour" ? "labour" : "part",
            description: art.name,
            quantity: 1,
            unit: art.unit || (art.category === "labour" ? "AW" : "Stk"),
            unit_price_gross: Number(gross.toFixed(2)),
        });
    };
    const addByEan = async (ean) => {
        try {
            const r = await api.get("/articles", { params: { search: ean, active_only: true, limit: 5 } });
            const arts = r.data?.articles || r.data || [];
            const hit = arts.find((a) => String(a.ean || "") === String(ean)) || arts[0];
            if (!hit) { toast.error(`Kein Artikel zu EAN ${ean} gefunden`); return; }
            addArticle(hit);
        } catch { toast.error("EAN-Suche fehlgeschlagen"); }
    };
    const addFreeText = (text) => {
        if (!String(text || "").trim()) return;
        quickAdd({ type: "part", description: String(text).trim(), quantity: 1, unit_price_gross: 0 });
    };

    const startTimer = async (stopOther = false) => {
        try {
            await api.post(`/work-orders/${wid}/timesheet/start`, stopOther ? { stop_other: true } : {});
            toast.success(stopOther ? "Anderer Timer beendet — Timer hier gestartet" : "Timer gestartet");
            await load();
        } catch (e) {
            const det = e.response?.data?.detail;
            if (det && det.code === "timer_running_elsewhere") {
                if (window.confirm(det.message)) return startTimer(true);
            } else {
                toast.error(typeof det === "string" ? det : "Timer konnte nicht gestartet werden");
            }
        }
    };
    const stopTimer = async () => {
        try { await api.post(`/work-orders/${wid}/timesheet/stop`); toast.success("Timer gestoppt"); await load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Timer konnte nicht gestoppt werden"); }
    };

    const uploadCapture = async (file, kind = null, setBusy = null) => {
        if (!file) return;
        setBusy?.(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (kind) fd.append("kind", kind);
            await api.post(`/work-orders/${wid}/capture-photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(file.type?.startsWith("video/") ? "Video hochgeladen" : "Foto hochgeladen");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
        finally { setBusy?.(false); }
    };
    const [uploading, setUploading] = useState(false);
    const photoUrl = (rel) => (rel?.startsWith("http") ? rel : `${process.env.REACT_APP_BACKEND_URL}${rel}`);
    const removePhoto = async (url) => {
        try {
            await api.delete(`/work-orders/${wid}/capture-photos`, { params: { url } });
            toast.success("Entfernt");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen"); }
    };

    // Jul-2026 (User-Wunsch): Monteur-Anmerkung per Sprache einsprechen (Web Speech API)
    const recogRef = useRef(null);
    const [listening, setListening] = useState(false);
    const toggleDictation = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { toast.error("Spracheingabe wird von diesem Browser nicht unterstützt."); return; }
        if (listening) { try { recogRef.current?.stop(); } catch { /* noop */ } setListening(false); return; }
        const rec = new SR();
        rec.lang = "de-DE";
        rec.continuous = true;
        rec.interimResults = false;
        rec.onresult = (e) => {
            let text = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) text += e.results[i][0].transcript;
            }
            if (text.trim()) {
                setMonteurNoteNone(false);
                setMonteurNote((prev) => {
                    const next = (prev ? prev.trim() + " " : "") + text.trim();
                    autosave({ monteur_note: next, monteur_note_none: false });
                    return next;
                });
            }
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => { setListening(false); toast.error("Spracheingabe abgebrochen — bitte Mikrofon-Zugriff erlauben."); };
        recogRef.current = rec;
        setListening(true);
        rec.start();
    };

    const [completing, setCompleting] = useState(false);
    const completeOrder = async () => {
        // Jul-2026 (User-Wunsch): Pflichtfelder vor Abschluss prüfen und benennen —
        // Abstellort nach Reparatur + Monteur-Anmerkung (oder „Keine Anmerkungen").
        const probs = [];
        if (!abstellortAfter.trim()) probs.push("Abstellort nach Reparatur");
        if (!monteurNote.trim() && !monteurNoteNone) probs.push("Monteur-Anmerkungen (oder „Keine Anmerkungen“ bestätigen)");
        if (probs.length) {
            setMissing(probs);
            toast.error(`Pflichtfelder fehlen: ${probs.join(" · ")}`);
            return;
        }
        setMissing([]);
        // Hinweis, wenn keine Arbeitszeit erfasst wurde (nicht blockierend)
        const tsData = data?.timesheet || {};
        const hasTime = Number(tsData.total_hours || 0) > 0 || (tsData.items || []).length > 0;
        if (!hasTime && !window.confirm("Es wurde keine Arbeitszeit erfasst. Auftrag trotzdem abschließen?")) return;
        setCompleting(true);
        try {
            await api.patch(`/work-orders/${wid}/capture-meta`, { monteur_note: monteurNote, monteur_note_none: monteurNoteNone });
            await api.post(`/work-orders/${wid}/complete-delivery`);
            toast.success("Auftrag fertig — bereit zur Abrechnung");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Auftrag konnte nicht abgeschlossen werden");
        } finally {
            setCompleting(false);
        }
    };

    if (authLoading || loading) {
        return <div className="min-h-screen flex items-center justify-center text-gray-300"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Auftrag…</div>;
    }
    if (!data) {
        return <div className="min-h-screen flex items-center justify-center text-gray-300">Auftrag nicht gefunden.</div>;
    }

    const order = data.order || {};
    const meta = data.delivery_meta || {};
    const cmeta = data.capture_meta || {};
    const ts = data.timesheet || { items: [], total_min: 0, active_entry: null };
    const v = order.vehicle || {};
    const positions = order.positions || [];
    const photos = data.capture_photos || [];
    const sollItems = [];
    const seen = new Set();
    [...(meta.accessories || []).map((a) => ({ ...a, _icon: "part" })),
     ...(meta.services || []).map((s) => ({ ...s, _icon: "labour" }))].forEach((x) => {
        const key = `${String(x.name || "").trim().toLowerCase()}|${x.qty || 1}`;
        if (seen.has(key)) return;
        seen.add(key);
        sollItems.push(x);
    });

    const MetaRow = ({ icon: Icon, label, value }) => (
        <div className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
            <Icon className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-300 font-bold">{label}</div>
                <div className="text-sm font-bold break-words">{value || "—"}</div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-24 mobile-capture-bright" data-testid="mobile-capture-page">
            {/* Kopf */}
            <div className="bg-[#111827] text-white px-4 py-4 sticky top-0 z-10">
                <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-[#FFCC00]" />
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#FFCC00] font-bold">Auftrags-Erfassung</div>
                        <div className="font-display font-black text-lg truncate" data-testid="mobile-capture-order-number">{order.order_number || "—"}</div>
                    </div>
                    <button onClick={load} className="ml-auto text-xs text-gray-200 inline-flex items-center gap-1" data-testid="mobile-capture-refresh">
                        <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                    </button>
                </div>
            </div>

            <div className="px-4 py-4 space-y-4 max-w-2xl mx-auto">
                {/* Auftragsdaten — Jul-2026 (User-Wunsch): nur Fahrzeug + geplante Auslieferung */}
                <div className="bg-amber-50/60 ring-2 ring-amber-300 rounded-sm p-4" data-testid="mobile-capture-meta">
                    <h2 className="font-display font-black text-sm uppercase tracking-wider text-gray-300 mb-2">Auftragsdaten</h2>
                    <MetaRow icon={Package} label="Fahrzeug" value={[v.brand, v.model].filter(Boolean).join(" ")} />
                    <MetaRow icon={CalendarClock} label="Geplante Auslieferung" value={fmtDate(meta.delivery_date)} />
                </div>

                {/* Standort / Fahrzeug — Autosave, Überschriften als Platzhalter */}
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="mobile-capture-location">
                    <h2 className="font-display font-black text-sm uppercase tracking-wider text-gray-300 mb-3">Standort / Fahrzeug</h2>
                    <div className="flex items-center gap-3 mb-3">
                        <input value={abstellort} onChange={(e) => { setAbstellort(e.target.value); autosave({ abstellort: e.target.value }); }} placeholder="Abstellort (z. B. Hof links, Platz 12)" className="flex-1 h-11 rounded-sm border border-gray-300 px-3 text-sm" data-testid="mobile-capture-abstellort" />
                        {/* Foto-Symbol nur, wenn bei der Abgabe ein Abstellort-Foto gemacht wurde */}
                        {cmeta.abstellort_photo && (
                            <img src={photoUrl(cmeta.abstellort_photo)} alt="Abstellort bei Abgabe" className="w-11 h-11 object-cover rounded-sm border border-gray-300 shrink-0" data-testid="mobile-capture-abstellort-photo" />
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input value={vehicleColor} onChange={(e) => { setVehicleColor(e.target.value); autosave({ vehicle_color: e.target.value }); }} placeholder="Fahrzeugfarbe" className="w-full h-11 rounded-sm border border-gray-300 px-3 text-sm" data-testid="mobile-capture-color" />
                        <input value={mechaniker} onChange={(e) => { setMechaniker(e.target.value); autosave({ mechaniker: e.target.value }); }} placeholder="Mechaniker" className="w-full h-11 rounded-sm border border-gray-300 px-3 text-sm" data-testid="mobile-capture-mechaniker" />
                    </div>
                </div>

                {/* Zubehör & Dienstleistungen (Soll) + Kundenauftrag im Wortlaut */}
                <div className="bg-amber-50/60 ring-2 ring-amber-300 rounded-sm p-4" data-testid="mobile-capture-items">
                    <h2 className="font-display font-black text-sm uppercase tracking-wider text-gray-300 mb-2">Zubehör & Dienstleistungen</h2>
                    {sollItems.length === 0 ? (
                        <p className="text-sm text-gray-300">Keine hinterlegt.</p>
                    ) : (
                        <ul className="space-y-1">
                            {sollItems.map((a, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm">
                                    {a._icon === "labour" ? <Wrench className="w-3.5 h-3.5 text-gray-300" /> : <Package className="w-3.5 h-3.5 text-gray-300" />}
                                    {a.name} <span className="text-gray-300">×{a.qty || 1}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {/* Jul-2026 (User-Wunsch): Kundenauftrag als bearbeitbares Freitextfeld */}
                    <div className="mt-3" data-testid="mobile-capture-complaint">
                        <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold mb-1">Kundenauftrag / Arbeitsauftrag (im Wortlaut)</div>
                        <textarea
                            value={complaintText}
                            onChange={(e) => { setComplaintText(e.target.value); autosave({ customer_complaint: e.target.value }); }}
                            placeholder="Was soll laut Kunde gemacht werden? (z. B. „Bremse quietscht hinten, bitte prüfen und ggf. Beläge tauschen“)"
                            rows={3}
                            className="w-full rounded-sm border-l-4 border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-gray-900"
                            data-testid="mobile-capture-complaint-input"
                        />
                    </div>
                </div>

                {/* Teile / Arbeit erfassen — WWS-Universalsuche + EAN-Scan (Jul-2026) */}
                <div className="bg-blue-50/40 ring-2 ring-blue-300 rounded-sm p-4" data-testid="mobile-capture-add">
                    <WwsAddBar
                        header={<h2 className="font-display font-black text-sm uppercase tracking-wider">Teile erfassen</h2>}
                        onAddArticle={addArticle}
                        onAddEan={addByEan}
                        onAddFreeText={addFreeText}
                        onCreateArticle={(q) => addFreeText(q)}
                        testidPrefix="mobile-wws"
                    />
                    {saving && <div className="mt-2 text-xs text-gray-300 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Speichere…</div>}
                    {positions.length > 0 && (
                        <ul className="mt-3 space-y-1 border-t border-gray-100 pt-3" data-testid="mobile-capture-positions">
                            {positions.map((p, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm">
                                    {p.type === "labour" ? <Wrench className="w-3.5 h-3.5 text-gray-300" /> : <Package className="w-3.5 h-3.5 text-gray-300" />}
                                    <span className="flex-1 truncate">{p.description}</span>
                                    <span className="text-gray-300 font-mono-num text-xs">×{p.quantity}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Arbeitszeiten — nur Timer (manuelle Minuten/Notiz entfernt, Jul-2026) */}
                <div className="bg-blue-50/40 ring-2 ring-blue-300 rounded-sm p-4" data-testid="mobile-capture-timesheet">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display font-black text-sm uppercase tracking-wider"><Clock className="w-4 h-4 inline mr-1 text-gray-300" /> Arbeitszeiten</h2>
                        <span className="text-xs font-mono-num font-bold">Σ {ts.total_hours ?? 0} Std</span>
                    </div>
                    {ts.active_entry ? (
                        <button type="button" onClick={stopTimer} className="w-full h-11 rounded-sm bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-1.5" data-testid="mobile-capture-timer-stop">
                            <Square className="w-4 h-4" /> Timer stoppen (läuft seit {fmtTime(ts.active_entry.started_at)})
                        </button>
                    ) : (
                        <button type="button" onClick={startTimer} className="w-full h-11 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-1.5" data-testid="mobile-capture-timer-start">
                            <Play className="w-4 h-4" /> Timer starten
                        </button>
                    )}
                    {ts.items.length > 0 && (
                        <ul className="mt-3 space-y-1 border-t border-gray-100 pt-3">
                            {ts.items.map((it) => (
                                <li key={it.id} className="flex items-center gap-2 text-sm">
                                    <Clock className="w-3.5 h-3.5 text-gray-300" />
                                    <span className="flex-1 truncate">{it.user_label || "Mitarbeiter"}{it.note ? ` · ${it.note}` : ""}</span>
                                    <span className="font-mono-num text-xs">{it.stopped_at ? `${it.duration_min} Min` : "läuft…"}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Schadensfotos (Foto + Video) — Jul-2026 (User-Wunsch) */}
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="mobile-capture-photos">
                    <h2 className="font-display font-black text-sm uppercase tracking-wider mb-3"><Camera className="w-4 h-4 inline mr-1 text-gray-300" /> Schadensfotos</h2>
                    <div className="flex flex-wrap gap-2">
                        {photos.map((p) => (
                            <div key={p} className="relative group" data-testid="mobile-capture-photo">
                                {isVideoUrl(p) ? (
                                    <video src={photoUrl(p)} className="w-20 h-20 object-cover rounded-sm border border-gray-200" controls muted />
                                ) : (
                                    <img src={photoUrl(p)} alt="Schadensfoto" className="w-20 h-20 object-cover rounded-sm border border-gray-200" />
                                )}
                                <button type="button" onClick={() => removePhoto(p)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-1" title="Entfernen" data-testid="mobile-capture-photo-remove">
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                        <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-[#FFCC00]" data-testid="mobile-capture-photo-upload">
                            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                            <span className="text-[9px] uppercase tracking-wider font-bold mt-0.5">Foto</span>
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadCapture(e.target.files?.[0], null, setUploading)} disabled={uploading} />
                        </label>
                        <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-[#FFCC00]" data-testid="mobile-capture-video-upload">
                            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
                            <span className="text-[9px] uppercase tracking-wider font-bold mt-0.5">Video</span>
                            <input type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => uploadCapture(e.target.files?.[0], null, setUploading)} disabled={uploading} />
                        </label>
                    </div>
                    <p className="text-[11px] text-gray-300 mt-2">Fotos max. 8 MB · Videos max. 50 MB.</p>
                </div>

                {/* Abstellort NACH der Reparatur (Foto + Hinweis) — Jul-2026 (User-Wunsch) */}
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="mobile-capture-after">
                    <h2 className="font-display font-black text-sm uppercase tracking-wider mb-3"><MapPin className="w-4 h-4 inline mr-1 text-gray-300" /> Abgestellt nach Reparatur</h2>
                    <div className="flex items-center gap-3">
                        <input value={abstellortAfter} onChange={(e) => { setAbstellortAfter(e.target.value); autosave({ abstellort_after: e.target.value }); }} placeholder="Wo wurde das Fahrzeug abgestellt? (z. B. Halle 2, Platz 5) *" className={`flex-1 h-11 rounded-sm border px-3 text-sm ${missing.includes("Abstellort nach Reparatur") && !abstellortAfter.trim() ? "border-red-500 ring-2 ring-red-200" : "border-gray-300"}`} data-testid="mobile-capture-abstellort-after" />
                        <label className="relative shrink-0" data-testid="mobile-capture-abstellort-after-upload">
                            {cmeta.abstellort_after_photo ? (
                                <img src={photoUrl(cmeta.abstellort_after_photo)} alt="Abstellort nach Reparatur" className="w-11 h-11 object-cover rounded-sm border border-gray-300" />
                            ) : (
                                <span className="w-11 h-11 border-2 border-dashed border-gray-300 rounded-sm flex items-center justify-center">
                                    {uploadingAbstAfter ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                                </span>
                            )}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadCapture(e.target.files?.[0], "abstellort_after", setUploadingAbstAfter)} disabled={uploadingAbstAfter} />
                        </label>
                    </div>
                </div>

                {/* Monteur-Anmerkungen — Pflicht vor Freigabe (Jul-2026, User-Wunsch) */}
                <div className={`bg-white border-2 rounded-sm p-4 ${missing.some((m) => m.startsWith("Monteur")) && !monteurNote.trim() && !monteurNoteNone ? "border-red-500" : "border-sky-400"}`} data-testid="mobile-capture-note">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="font-display font-black text-sm uppercase tracking-wider"><ClipboardList className="w-4 h-4 inline mr-1 text-gray-300" /> Monteur-Anmerkungen *</h2>
                        {/* Jul-2026 (User-Wunsch): Anmerkung per Sprache einsprechen */}
                        <button type="button" onClick={toggleDictation}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-sm font-bold text-xs uppercase tracking-wider ${listening ? "bg-red-600 text-white animate-pulse" : "bg-sky-600 text-white"}`}
                            data-testid="mobile-capture-dictate-btn">
                            {listening ? <><MicOff className="w-4 h-4" /> Stopp</> : <><Mic className="w-4 h-4" /> Einsprechen</>}
                        </button>
                    </div>
                    {listening && <div className="mb-2 text-xs text-red-600 font-bold" data-testid="mobile-capture-dictate-hint">● Aufnahme läuft — bitte sprechen…</div>}
                    <textarea
                        value={monteurNote}
                        onChange={(e) => { setMonteurNote(e.target.value); if (e.target.value.trim()) setMonteurNoteNone(false); autosave({ monteur_note: e.target.value }); }}
                        placeholder="Anmerkungen des Monteurs (z. B. festgestellte Mängel, Empfehlungen) — Pflicht vor der Abrechnungs-Freigabe"
                        rows={3}
                        className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm"
                        data-testid="mobile-capture-monteur-note"
                    />
                    <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={monteurNoteNone} onChange={(e) => { setMonteurNoteNone(e.target.checked); autosave({ monteur_note_none: e.target.checked }); }} data-testid="mobile-capture-note-none" />
                        Keine Anmerkungen
                    </label>
                </div>

                {/* Auftrag abschließen */}
                {["invoiced", "completed"].includes(order.status) ? (
                    <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-4 text-center" data-testid="mobile-capture-completed">
                        <CheckCircle2 className="w-7 h-7 mx-auto mb-1 text-emerald-600" />
                        <div className="font-display font-black text-sm text-emerald-800">
                            {order.status === "invoiced" ? "Auftrag abgerechnet" : "Auftrag fertig — bereit zur Abrechnung"}
                        </div>
                    </div>
                ) : (
                    <button type="button" onClick={completeOrder} disabled={completing}
                        className="w-full h-14 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="mobile-capture-complete-btn">
                        {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5" /> Auftrag fertig & zur Abrechnung</>}
                    </button>
                )}

                <div className="text-center text-xs text-gray-300 pt-1">
                    <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-500" /> Änderungen werden automatisch gespeichert.
                </div>
            </div>
        </div>
    );
}
